import React, { useMemo, useState, useEffect } from 'react';
import { Sidebar } from '../../../components/organisms/Sidebar';
import * as LucideIcons from 'lucide-react';

interface EmojiInfo {
  emoji: string;
  name: string;
  unicode: string;
  usage: string[];
  defaultIcon?: string;
}

const emojis: EmojiInfo[] = [
  {
    emoji: '📄',
    name: 'Document',
    unicode: 'U+1F4C4',
    usage: [
      'resume-editor.tsx',
      'new-base-resume.tsx',
      'resume-builder.tsx',
      'BoardHolder.tsx',
      'EditJobModal.tsx',
      'NewPDFDocumentModal.tsx',
      'Sidebar.tsx'
    ],
    defaultIcon: 'FileText'
  },
  {
    emoji: '🎨',
    name: 'Artist Palette',
    unicode: 'U+1F3A8',
    usage: ['resume-editor.tsx']
  },
  {
    emoji: '💡',
    name: 'Light Bulb',
    unicode: 'U+1F4A1',
    usage: ['resume-editor.tsx']
  },
  {
    emoji: '⚡',
    name: 'Lightning',
    unicode: 'U+26A1',
    usage: ['resume-editor.tsx'],
    defaultIcon: 'Zap'
  },
  {
    emoji: '🚀',
    name: 'Rocket',
    unicode: 'U+1F680',
    usage: ['ProjectsTab.tsx']
  },
  {
    emoji: '🎓',
    name: 'Graduation Cap',
    unicode: 'U+1F393',
    usage: ['EducationTab.tsx', 'EducationCard.tsx']
  },
  {
    emoji: '🏆',
    name: 'Trophy',
    unicode: 'U+1F3C6',
    usage: ['CertificationCard.tsx', 'BoardHolder.tsx'],
    defaultIcon: 'Trophy'
  },
  {
    emoji: '💼',
    name: 'Briefcase',
    unicode: 'U+1F4BC',
    usage: ['ContactsModal.tsx', 'ContactCard.tsx', 'BoardHolder.tsx', 'KanbanTabs.tsx'],
    defaultIcon: 'BriefcaseBusiness'
  },
  {
    emoji: '📊',
    name: 'Bar Chart',
    unicode: 'U+1F4CA',
    usage: ['KanbanTabs.tsx'],
    defaultIcon: 'BarChart3'
  },
  {
    emoji: '📝',
    name: 'Memo',
    unicode: 'U+1F4DD',
    usage: ['NotesTab.tsx'],
    defaultIcon: 'FileText'
  },
  {
    emoji: '🏢',
    name: 'Office Building',
    unicode: 'U+1F3E2',
    usage: ['JobKanbanCard.tsx', 'ExperienceTab.tsx', 'VolunteerExperienceTab.tsx'],
    defaultIcon: 'Building2'
  },
  {
    emoji: '🗑️',
    name: 'Wastebasket',
    unicode: 'U+1F5D1',
    usage: [
      'JobKanbanCard.tsx',
      'Sidebar.tsx',
      'EditProjectForm.tsx',
      'EditExperienceForm.tsx',
      'ProjectCard.tsx',
      'ExperienceCard.tsx',
      'NotesTab.tsx',
      'AddProjectModal.tsx',
      'AddExperienceModal.tsx',
      'AddEducationModal.tsx',
      'ActivitiesList.tsx'
    ],
    defaultIcon: 'Trash2'
  },
  {
    emoji: '🔗',
    name: 'Link',
    unicode: 'U+1F517',
    usage: [
      'BioEditor.tsx',
      'ProjectsTab.tsx',
      'LinksTab.tsx',
      'EditTab.tsx',
      'NotesTab.tsx'
    ],
    defaultIcon: 'Link2'
  },
  {
    emoji: '✨',
    name: 'Sparkles',
    unicode: 'U+2728',
    usage: [
      'DocumentTile.tsx',
      'BoardHolder.tsx',
      'resume-editor.tsx',
      'SkillsSection.tsx',
      'EditProjectForm.tsx',
      'EditExperienceForm.tsx',
      'ProjectCard.tsx',
      'ExperienceCard.tsx',
      'EditExperienceModal.tsx',
      'Sidebar.tsx'
    ],
    defaultIcon: 'Sparkles'
  },
  {
    emoji: '✉️',
    name: 'Envelope',
    unicode: 'U+2709',
    usage: ['ContactCard.tsx', 'ContactsModal.tsx', 'EmailBlock.tsx'],
    defaultIcon: 'Mail'
  },
  {
    emoji: '✉',
    name: 'Envelope (no variation)',
    unicode: 'U+2709',
    usage: ['AccountSettingsModal.tsx', 'EmailInput.tsx'],
    defaultIcon: 'Mail'
  },
  {
    emoji: '📞',
    name: 'Telephone',
    unicode: 'U+1F4DE',
    usage: ['ContactCard.tsx', 'ContactsModal.tsx', 'PhoneBlock.tsx'],
    defaultIcon: 'Phone'
  },
  {
    emoji: '📍',
    name: 'Round Pushpin',
    unicode: 'U+1F4CD',
    usage: ['ContactCard.tsx'],
    defaultIcon: 'MapPin'
  },
  {
    emoji: '🐦',
    name: 'Bird',
    unicode: 'U+1F426',
    usage: ['ContactCard.tsx', 'ContactsModal.tsx'],
    defaultIcon: 'Twitter'
  },
  {
    emoji: '📘',
    name: 'Blue Book',
    unicode: 'U+1F4D8',
    usage: ['ContactCard.tsx', 'ContactsModal.tsx'],
    defaultIcon: 'Book'
  },
  {
    emoji: '🐙',
    name: 'Octopus',
    unicode: 'U+1F419',
    usage: ['ContactCard.tsx', 'ContactsModal.tsx'],
    defaultIcon: 'Github'
  },
  {
    emoji: '🎯',
    name: 'Direct Hit',
    unicode: 'U+1F3AF',
    usage: ['resume-builder.tsx'],
    defaultIcon: 'Target'
  },
  {
    emoji: '📋',
    name: 'Clipboard',
    unicode: 'U+1F4CB',
    usage: [
      'BoardHolder.tsx',
      'resume-editor.tsx',
      'Activities.tsx',
      'ActivitiesTab.tsx'
    ],
    defaultIcon: 'ClipboardList'
  },
  {
    emoji: '⚙️',
    name: 'Gear',
    unicode: 'U+2699',
    usage: ['Sidebar.tsx'],
    defaultIcon: 'Settings'
  },
  {
    emoji: '🏠',
    name: 'House',
    unicode: 'U+1F3E0',
    usage: ['Sidebar.tsx'],
    defaultIcon: 'Home'
  },
  {
    emoji: '👁️',
    name: 'Eye',
    unicode: 'U+1F441',
    usage: ['Sidebar.tsx'],
    defaultIcon: 'Eye'
  },
  {
    emoji: '👤',
    name: 'Bust in Silhouette',
    unicode: 'U+1F464',
    usage: ['Sidebar.tsx'],
    defaultIcon: 'User'
  },
  {
    emoji: '👥',
    name: 'Busts in Silhouette',
    unicode: 'U+1F465',
    usage: ['Sidebar.tsx'],
    defaultIcon: 'Users'
  },
  {
    emoji: '📁',
    name: 'File Folder',
    unicode: 'U+1F4C1',
    usage: ['Sidebar.tsx'],
    defaultIcon: 'Folder'
  },
  {
    emoji: '⚠️',
    name: 'Warning Sign',
    unicode: 'U+26A0',
    usage: ['resume-editor.tsx'],
    defaultIcon: 'AlertTriangle'
  },
  {
    emoji: '✏️',
    name: 'Pencil',
    unicode: 'U+270F',
    usage: [
      'resume-editor.tsx',
      'SectionOrderTitles.tsx',
      'EditProjectForm.tsx',
      'EditExperienceForm.tsx',
      'StatusDropdown.tsx',
      'ProjectCard.tsx',
      'ExperienceCard.tsx'
    ],
    defaultIcon: 'Pencil'
  },
  {
    emoji: '✍️',
    name: 'Writing Hand',
    unicode: 'U+1F58D',
    usage: ['resume-editor.tsx'],
    defaultIcon: 'Signature'
  },
  {
    emoji: '💜',
    name: 'Purple Heart',
    unicode: 'U+1F49C',
    usage: ['resume-editor.tsx'],
    defaultIcon: 'HeartPulse'
  },
  {
    emoji: '🏪',
    name: 'Convenience Store',
    unicode: 'U+1F3EA',
    usage: ['resume-editor.tsx'],
    defaultIcon: 'Store'
  },
  {
    emoji: '🖥️',
    name: 'Desktop Computer',
    unicode: 'U+1F5A5',
    usage: ['EditExperienceForm.tsx', 'EditExperienceModal.tsx'],
    defaultIcon: 'Monitor'
  },
  {
    emoji: '🌐',
    name: 'Globe with Meridians',
    unicode: 'U+1F310',
    usage: ['EditExperienceForm.tsx', 'EditExperienceModal.tsx'],
    defaultIcon: 'Globe2'
  }
];

const EmojisDebugPage: React.FC = () => {
  useEffect(() => {
    document.title = 'OfferFlow | Emojis';
  }, []);

  const [iconMapping, setIconMapping] = useState<Record<string, string>>({});

  const lucideIconNames = useMemo(
    () => Object.keys(LucideIcons).sort(),
    []
  );

  const handleIconChange = (emoji: string, iconName: string) => {
    setIconMapping((prev) => ({
      ...prev,
      [emoji]: iconName,
    }));
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '40px', backgroundColor: '#f9fafb' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '600', 
            marginBottom: '8px',
            color: '#111827'
          }}>
            Emoji Reference
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#6b7280', 
            marginBottom: '32px' 
          }}>
            All emojis used in the frontend application
          </p>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ 
                  backgroundColor: '#f9fafb',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Emoji
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Name
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Unicode
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Lucide icon
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Usage
                  </th>
                </tr>
              </thead>
              <tbody>
                {emojis.map((emojiInfo, index) => (
                  <tr 
                    key={index}
                    style={{ 
                      borderBottom: '1px solid #e5e7eb',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <td style={{ 
                      padding: '16px',
                      fontSize: '24px',
                      textAlign: 'center'
                    }}>
                      {emojiInfo.emoji}
                    </td>
                    <td style={{ 
                      padding: '16px',
                      fontWeight: '500',
                      color: '#111827'
                    }}>
                      {emojiInfo.name}
                    </td>
                    <td style={{ 
                      padding: '16px',
                      fontFamily: 'monospace',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}>
                      {emojiInfo.unicode}
                    </td>
                    <td style={{ padding: '16px', minWidth: '260px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <select
                          value={iconMapping[emojiInfo.emoji] || emojiInfo.defaultIcon || ''}
                          onChange={(e) => handleIconChange(emojiInfo.emoji, e.target.value)}
                          style={{
                            flex: 1,
                            padding: '6px 8px',
                            borderRadius: '4px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px',
                            color: '#111827',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value="">—</option>
                          {lucideIconNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <div style={{ width: '32px', textAlign: 'center' }}>
                          {(() => {
                            const iconName = iconMapping[emojiInfo.emoji] || emojiInfo.defaultIcon;
                            const IconComponent = iconName ? (LucideIcons as any)[iconName] : null;
                            return IconComponent ? <IconComponent size={20} /> : null;
                          })()}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {emojiInfo.usage.map((file, fileIndex) => (
                          <span
                            key={fileIndex}
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#eff6ff',
                              color: '#1e40af',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontFamily: 'monospace',
                              fontWeight: '500'
                            }}
                          >
                            {file}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #fde68a'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '14px', 
              color: '#92400e',
              lineHeight: '1.5'
            }}>
              <strong>Total emojis:</strong> {emojis.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmojisDebugPage;

